import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Header from '../boarddetail/components/Header';
import Footer from 'examples/Footer';
import { apiGetNotice, apiPatchNotice, apiFileUpload } from '../../../api/notice.api';
import { Buffer } from 'buffer'; // Buffer 라이브러리 임포트
import {useData} from '../../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';

const BoardEdit = () => {
  const { id } = useParams(); // URL 파라미터에서 id를 가져옴
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.notice || null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    subType: '',
    orderno: '',
  });
  const [file, setFile] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const {user, appIsReady} = useData();

  useEffect(() => {
    if (!notice && id) {
      if(appIsReady && user){
        fetchNotice(id);
      }
    } else {
      setLoading(false);
      setFormData({
        title: notice.title || '',
        content: notice.content || '',
        subType: notice.subType || '',
        orderno: notice.orderno || '',
      });
    }
  }, [id, notice, user, appIsReady]);


  const fetchNotice = async (noticeid) => {
    setLoading(true);
    try {
      const response = await apiGetNotice(noticeid, user.accessToken);
      if (response && response.content && response.content.data) {
        const decodedContent = Buffer.from(response.content.data).toString('utf8');
        setNotice({ ...response, content: decodedContent });
        setFormData({
          title: response.title || '',
          content: decodedContent,
          subType: response.subType || '',
          orderno: response.orderno || '',
        });
      } else {
        setNotice(response);
      }
    } catch (error) {
      console.error('Error fetching notice:', error);
    } finally {
      setLoading(false);
    }
  };

  const messageTypeToString = (type) => {
    switch (type) {
      case 1: return '앱공지';
      case 3: return '매뉴얼';
      case 4: return '점주용공지';
      case 5: return '점주용매뉴얼';
      case 6: return '후원사';
      default: return '기타';
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSave = async () => {
    const filteredFormData = { ...formData };
    if (location.state.messageType !== 3 && location.state.messageType !== 5 && location.state.messageType !== 6) {
      delete filteredFormData.subType;
      delete filteredFormData.orderno;
    } else {
      filteredFormData.subType = formData.subType;
      filteredFormData.orderno = formData.orderno;
    }

    try {
      await apiPatchNotice(filteredFormData, id);
      navigate(`/board/detail/${id}`, { state: { messageType: location.state.messageType, page: location.state.page } });
    } catch (error) {
      console.error('Error updating notice:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      return;
    }
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };


  const handleUpload = async () => {
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await apiFileUpload(uploadFormData);
      const imageUrl = response;
      setUploadedImages([...uploadedImages, imageUrl]);

      console.log("notice.messageType=" + notice.messageType);
      if(notice.messageType === 5){
        setFormData({
          ...formData,
          content: `${imageUrl}`
        });
      }else{
       setFormData({
          ...formData,
          content: `${formData.content}\n<img src="${imageUrl}" width="100%">\n`
        });
      }
      setFile(null); // Clear the file input
      setPreviewImage(null); // Clear the preview image
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <TopNavbar />
        <Header />
        <MDBox pt={6} pb={3}>
          <MDTypography variant="h4" color="info">
            Loading...
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const showSubTypeAndOrderno = location.state.messageType === 3 || location.state.messageType === 5 || location.state.messageType === 6;

  return (
    <DashboardLayout>
      <TopNavbar />
      <Header>
        <MDBox pt={6} pb={3}>
          {notice ? (
            <>
              <MDBox mb={2} display="flex" justifyContent="space-between">
                <MDTypography variant="h4" fontWeight="bold">
                  {messageTypeToString(notice.messageType)}
                </MDTypography>
                <Button variant="contained" color="success" onClick={handleSave}>
                  저장
                </Button>
              </MDBox>
              <MDBox mb={2} padding={1.5} borderRadius={10}>
                <TextField
                  fullWidth
                  label="제목"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="내용"
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  multiline
                  rows={15}
                />
                {showSubTypeAndOrderno && (
                  <>
                    <TextField
                      fullWidth
                      label="서브 타입"
                      name="subType"
                      value={formData.subType}
                      onChange={handleChange}
                      variant="outlined"
                      margin="normal"
                    />
                    <TextField
                      fullWidth
                      label="순서"
                      name="orderno"
                      value={formData.orderno}
                      onChange={handleChange}
                      variant="outlined"
                      margin="normal"
                    />
                  </>
                )}
              </MDBox>
              <MDBox mt={2} display="flex" alignItems="center">
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="contained-button-file"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="contained-button-file">
                  <Button variant="contained" component="span" color="info">
                    파일 선택
                  </Button>
                </label>
                <Button variant="contained" color="success" onClick={handleUpload} sx={{ marginLeft: 2 }}>
                  업로드
                </Button>
              </MDBox>
              <MDBox mt={2}>
                {previewImage && (
                  <img src={previewImage} alt="preview" style={{ width: '20%', marginBottom: 10 }} />
                )}
                {uploadedImages.map((url, index) => (
                  <img key={index} src={url} alt={`uploaded ${index}`} style={{ width: '20%', marginBottom: 10 }} />
                ))}
              </MDBox>
              <MDBox mt={2}>
                <Button variant="contained" color="success" onClick={() => navigate(-1)}>
                  뒤로 가기
                </Button>
              </MDBox>
            </>
          ) : (
            <MDTypography variant="h4" color="info">
              Notice not found
            </MDTypography>
          )}
        </MDBox>
      </Header>
      <Footer />
    </DashboardLayout>
  );
}

export default BoardEdit;
